/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { dashboardsDelete } from "../funcs/dashboardsDelete.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DeleteDashboardRequest,
  DeleteDashboardSecurity,
} from "../models/operations/deletedashboard.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type DeleteDashboardMutationVariables = {
  request: DeleteDashboardRequest;
  security?: DeleteDashboardSecurity | undefined;
  options?: RequestOptions;
};

export type DeleteDashboardMutationData = void;

export type DeleteDashboardMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * deleteDashboard dashboards
 *
 * @remarks
 * Delete a dashboard and its cards. Its widgets stay saved. Its creator can; deleting someone else's needs project write access.
 */
export function useDeleteDashboardMutation(
  options?: MutationHookOptions<
    DeleteDashboardMutationData,
    DeleteDashboardMutationError,
    DeleteDashboardMutationVariables
  >,
): UseMutationResult<
  DeleteDashboardMutationData,
  DeleteDashboardMutationError,
  DeleteDashboardMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildDeleteDashboardMutation(client, options),
    ...options,
  });
}

export function mutationKeyDeleteDashboard(): MutationKey {
  return ["@gram/client", "dashboards", "delete"];
}

export function buildDeleteDashboardMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: DeleteDashboardMutationVariables,
  ) => Promise<DeleteDashboardMutationData>;
} {
  return {
    mutationKey: mutationKeyDeleteDashboard(),
    mutationFn: function deleteDashboardMutationFn({
      request,
      security,
      options,
    }): Promise<DeleteDashboardMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(dashboardsDelete(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
